import React, { useState } from 'react';

import {
  PopUp,
  Dialog,
  DialogHeader,
  DialogContent,
  Button,
  Spinner,
  Notification,
  ScrollableContainer,
} from '@ergeon/core-components';
import { ArrowDownTrayIcon } from '@ergeon/icons/hero/micro';

import { type ModalHandler } from '../index';
import { useModalClose } from '../../hooks/useModalClose';
import { formatEmailDate } from '../../utils/dateFormatters';
import { EmailRenderer } from '../../components/EmailRenderer';

import { useEmlParser } from './useEmlParser';
import { formatEmailAddress, downloadBase64File } from './utils';

type Props = {
  modal: ModalHandler;
  emlUrl: string;
  subject?: string;
};

const EmailPreviewModal: React.FC<Props> = ({ modal, emlUrl, subject: initialSubject }) => {
  const closeModal = useModalClose(modal);
  const { email, loading, error } = useEmlParser(emlUrl);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const handleDownload = (name: string, contentType: string, data64: string) => {
    setDownloadError(null);
    const success = downloadBase64File(name, contentType, data64);
    if (!success) {
      setDownloadError(`Failed to download "${name}". Please try again.`);
    }
  };

  const displaySubject = email?.subject || initialSubject || 'Email Preview';

  return (
    <PopUp isVisible={modal.visible} maxWidth="xlarge" onChangeVisible={closeModal}>
      <Dialog isVisible={modal.visible}>
        <DialogHeader onDismiss={closeModal} title={displaySubject} />
        <DialogContent>
          {loading && (
            <div className="flex items-center justify-center h-64">
              <Spinner active color="lightgray" size={40} />
            </div>
          )}

          {error && (
            <Notification mode="embed" type="Error">
              Failed to load email: {error}
            </Notification>
          )}

          {downloadError && (
            <Notification mode="embed" type="Error">
              {downloadError}
            </Notification>
          )}

          {!loading && !error && email && (
            <ScrollableContainer className="max-h-[70vh]" direction="col" gap={4}>
              <div className="flex flex-col gap-2 p-3 bg-gray-50 dark:bg-gray-800 rounded">
                <div className="flex gap-2">
                  <span className="paragraph-2 text-gray-600 dark:text-gray-400 shrink-0">From:</span>
                  <span className="paragraph-2 text-gray-900 dark:text-white break-all">{formatEmailAddress(email.from)}</span>
                </div>
                <div className="flex gap-2">
                  <span className="paragraph-2 text-gray-600 dark:text-gray-400 shrink-0">To:</span>
                  <span className="paragraph-2 text-gray-900 dark:text-white break-all">{formatEmailAddress(email.to)}</span>
                </div>
                {email.replyTo && (
                  <div className="flex gap-2">
                    <span className="paragraph-2 text-gray-600 dark:text-gray-400 shrink-0">Reply-To:</span>
                    <span className="paragraph-2 text-gray-900 dark:text-white break-all">{formatEmailAddress(email.replyTo)}</span>
                  </div>
                )}
                {email.cc && (
                  <div className="flex gap-2">
                    <span className="paragraph-2 text-gray-600 dark:text-gray-400 shrink-0">CC:</span>
                    <span className="paragraph-2 text-gray-900 dark:text-white break-all">{formatEmailAddress(email.cc)}</span>
                  </div>
                )}
                <div className="flex gap-2">
                  <span className="paragraph-2 text-gray-600 dark:text-gray-400 shrink-0">Date:</span>
                  <span className="paragraph-2 text-gray-900 dark:text-white">{formatEmailDate(email.date)}</span>
                </div>
              </div>

              <EmailRenderer html={email.html} text={email.text} />

              {email.attachments.length > 0 && (
                <div className="flex flex-col gap-2">
                  <span className="subheading-2 text-gray-900 dark:text-white">Attachments ({email.attachments.length})</span>
                  <div className="flex flex-wrap gap-2">
                    {email.attachments.map((attachment, index) => (
                      <Button
                        flavor="regular"
                        key={index}
                        onClick={() =>
                          handleDownload(
                            attachment.name,
                            attachment.contentType,
                            attachment.data64,
                          )}
                        size="small"
                        taste="line">
                        <ArrowDownTrayIcon className="size-4" />
                        {attachment.name}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                <span className="paragraph-2 text-gray-600 dark:text-gray-400">Source:</span>
                <a
                  className="paragraph-2 text-blue-600 dark:text-blue-400 hover:underline"
                  download="email.eml"
                  href={emlUrl}>
                  Download EML
                </a>
              </div>
            </ScrollableContainer>
          )}
        </DialogContent>
      </Dialog>
    </PopUp>
  );
};

export default EmailPreviewModal;
